import { ReactElement, FC, ChangeEvent } from "react";
import { LooseObject } from "../interfaces/LooseObject";
import {
    Box,
    Button,
    Container,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
} from "@mui/material";
import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { Logo } from "./Logo";
import SnackBar from "./SnackBar";
import LoadingOverlay from "react-loading-overlay-ts";
import { SignUpService } from "../services/signup.service";
import { RoleService, IRole } from "../services/role.service";
import { StringUtils } from "../utils/string.utils";

const Signup: FC<LooseObject> = (props: any): ReactElement => {
    const [loading, setLoading] = React.useState(false);

    const [snackBarMessage, setSnackBarMessage] = React.useState("");
    const [snackBarOpen, setSnackBarOpen] = React.useState(false);
    const [snackBarType, setSnackBarType] = React.useState("success");
    const handleSnackBarClose = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSnackBarOpen(false);
    };

    const [isSent, setisSent] = React.useState(false);

    const [roleList, setRoleList] = React.useState<IRole[]>([]);

    const [formData, setFormData] = React.useState({
        name: '',
        companyName: '',
        contactNumber: '',
        email: '',
        password: '',
        confirmPassword: '',
        otp: '',
        roleId: '',
    });

    // State to manage validation errors
    const [errors, setErrors] = React.useState({
        name: '',
        companyName: '',
        contactNumber: '',
        email: '',
        password: '',
        confirmPassword: '',
        otp: '',
        roleId: '',
    });


    // Function to handle form input changes
    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        // Update form data
        setFormData({
            ...formData,
            [name]: value,
        });

        // Validate the field and update errors
        validateField(name, value);
    };

    const handleRoleChange = (event: SelectChangeEvent<string>) => {
        const value = event.target.value as string;
        setFormData({
            ...formData,
            roleId: value,
        });
        validateField('roleId', value);
    };

    // Returns the error message for a single field (empty string when valid).
    // Kept as a pure function (rather than reading straight from `errors` state)
    // so callers that need the up-to-date validity of *all* fields at once
    // (e.g. on submit) aren't stuck reading last render's stale state.
    const getFieldError = (fieldName: string, value: string): string => {
        switch (fieldName) {
            case 'name':
                return !value ? 'Name is required.' : '';
            case 'email':
                if (!value) return 'Email is required.';
                return StringUtils.isValidEmail(value) ? '' : 'Please enter a valid email address.';
            case 'companyName':
                return value.length > 250 ? "Company Name can't be longer than 250 characters." : '';
            case 'contactNumber':
                if (!value) return '';
                if (value.length > 20) return "Contact Number can't be longer than 20 characters.";
                return /^[0-9+()\-.\s]+$/.test(value) ? '' : 'Please enter a valid contact number.';
            case 'roleId':
                return !value ? 'Role is required.' : '';
            case 'password':
                return StringUtils.validatePassword(value, formData.email);
            case 'confirmPassword':
                if (!value) return 'Confirm Password is required.';
                return value === formData.password ? '' : 'Confirm password do not match.';
            case 'otp':
                if (!isSent) return '';
                return !value ? 'OTP is required.' : '';
            default:
                return '';
        }
    };

    // Function to validate a specific field (updates the on-screen error as the user types)
    const validateField = (fieldName: string, value: string) => {
        const error = getFieldError(fieldName, value);
        setErrors((prevErrors) => ({
            ...prevErrors,
            [fieldName]: error,
        }));
    };


    const SignupService = new SignUpService();
    const roleService = new RoleService();
    const navigate = useNavigate();

    React.useEffect(() => {
        roleService
            .getRegistrationRoles()
            .then((result: any) => {
                setRoleList(result || []);
            })
            .catch(() => {
                setRoleList([]);
            });
    }, []);

    const handleKeyPress = (event: any) => {
        if (event.key === "Enter") {
            handleSignUp();
        }
    };
    const resetUserState = () => {
        setFormData({
            name: '',
            companyName: '',
            contactNumber: '',
            email: '',
            password: '',
            confirmPassword: '',
            otp: '',
            roleId: '',
        });
    };
    const handleSignUp = () => {
        if (loading) return; // guard against double-submit while a request is in flight

        // Compute validity synchronously rather than reading the (still-stale,
        // pre-update) `errors` state right after calling validateField -- doing
        // that let a blank form pass validation on the very first click.
        const newErrors = {
            name: getFieldError("name", formData.name),
            companyName: getFieldError("companyName", formData.companyName),
            contactNumber: getFieldError("contactNumber", formData.contactNumber),
            roleId: getFieldError("roleId", formData.roleId),
            email: getFieldError("email", formData.email),
            password: getFieldError("password", formData.password),
            confirmPassword: getFieldError("confirmPassword", formData.confirmPassword),
            otp: getFieldError("otp", formData.otp),
        };
        setErrors(newErrors);

        if (!Object.values(newErrors).every((error) => error === '')) {
            return;
        }

        setLoading(true);

        // Check if an OTP has been sent
        if (!isSent) {
            SignupService
                .sendEmailOtp({ email: formData.email, password: formData.password, isRegister:true })
                .then((result: any) => {
                    if (result) {
                        setisSent(true);
                        setSnackBarMessage("Please enter the OTP sent to your email.");
                        setSnackBarType("success");
                        setSnackBarOpen(true);
                    } else {
                        setSnackBarMessage("Failed to send OTP. Please try again.");
                        setSnackBarType("error");
                        setSnackBarOpen(true);
                    }
                    setLoading(false);
                })
                .catch((err: any) => {
                    setLoading(false);
                    setSnackBarMessage(StringUtils.toErrorMessage(err));
                    setSnackBarType("error");
                    setSnackBarOpen(true);
                });
        } else {
            // Make the registration API call
            SignupService
                .register({ name: formData.name, companyName: formData.companyName, contactNumber: formData.contactNumber, email: formData.email, password: formData.password, otp: formData.otp, roleId: formData.roleId })
                .then((result: any) => {
                    if (result) {
                                resetUserState();
                                setisSent(false)
                                setSnackBarMessage("Registered Successfully");
                                setSnackBarType("success");
                                setSnackBarOpen(true);
                                // show message briefly then redirect to login
                                setTimeout(() => navigate("/"), 1800);
                            } else {
                        setSnackBarMessage("Failed to register. Please try again.");
                        setSnackBarType("error");
                        setSnackBarOpen(true);
                    }
                    setLoading(false);
                })
                .catch((err: any) => {
                    setLoading(false);
                    setSnackBarMessage(StringUtils.toErrorMessage(err));
                    setSnackBarType("error");
                    setSnackBarOpen(true);
                });
        }
    };

    const LogoProps: any = {
        height: "auto",
        width: "100%",
    };

    return (
        <React.Fragment>
            <Container
                sx={{
                    maxWidth: "100%",
                    width: "100%",
                    padding: "15px",
                    textAlign: "center",
                }}
            >
                <LoadingOverlay active={loading} spinner className="hsc-loader">
                    <Box
                        sx={{
                            padding: "25px 10%",
                            textAlign: "center",
                            background: "#f8f6f6", width: "auto", // Changed width to auto for responsiveness
                            maxWidth: "500px", // Added max-width for responsiveness
                            marginLeft: "auto", // Centering the box horizontally
                            marginRight: "auto", // Centering the box horizontally

                        }}
                        onKeyPress={handleKeyPress}
                    >
                        <SnackBar
                            id="snackbar-signup"
                            message={snackBarMessage}
                            type={snackBarType}
                            open={snackBarOpen}
                            close={handleSnackBarClose}
                        />
                        <div style={{ width: "100%" }}>
                            <Logo {...LogoProps} style={{ maxWidth: "400px" }} />
                        </div>
                        <FormControl size="small" fullWidth sx={{ marginBlock: "5px" }}>
                            <TextField
                                sx={{ marginBlock: "10px" }}
                                required
                                id="outlined-name"
                                label="Name"
                                placeholder="Name"
                                size="small"
                                name="name"
                                disabled={isSent}
                                value={formData.name}
                                onChange={handleInputChange}
                                error={errors.name != ""}
                                helperText={errors.name}
                            />
                        </FormControl>
                        <FormControl size="small" fullWidth sx={{ marginBlock: "5px" }}>
                            <TextField
                                sx={{ marginBlock: "10px" }}
                                required
                                id="outlined-email"
                                label="Email"
                                placeholder="Email"
                                size="small"
                                name="email"
                                disabled={isSent}
                                value={formData.email}
                                onChange={handleInputChange}
                                error={errors.email != ""}
                                helperText={errors.email}
                            />
                        </FormControl>
                        <FormControl size="small" fullWidth sx={{ marginBlock: "5px" }}>
                            <InputLabel id="registration-role-label">Role</InputLabel>
                            <Select
                                labelId="registration-role-label"
                                id="registration-role"
                                name="roleId"
                                value={formData.roleId}
                                label="Role"
                                disabled={isSent}
                                onChange={handleRoleChange}
                                error={errors.roleId != ""}
                            >
                                <MenuItem value="">Select Role</MenuItem>
                                {roleList.map((role) => (
                                    <MenuItem key={role.id} value={String(role.id)}>
                                        {role.displayName || role.name}
                                    </MenuItem>
                                ))}
                            </Select>
                            {errors.roleId && (
                                <Box sx={{ color: '#d32f2f', fontSize: '0.75rem', marginTop: '4px', textAlign: 'left' }}>
                                    {errors.roleId}
                                </Box>
                            )}
                        </FormControl>
                        <FormControl size="small" fullWidth sx={{ marginBlock: "5px" }}>
                            <TextField
                                sx={{ marginBlock: "10px" }}
                                id="outlined-company-name"
                                label="Company Name"
                                placeholder="Company Name"
                                size="small"
                                name="companyName"
                                disabled={isSent}
                                value={formData.companyName}
                                onChange={handleInputChange}
                                error={errors.companyName != ""}
                                helperText={errors.companyName}
                            />
                        </FormControl>
                        <FormControl size="small" fullWidth sx={{ marginBlock: "5px" }}>
                            <TextField
                                sx={{ marginBlock: "10px" }}
                                id="outlined-contact-number"
                                label="Contact Number"
                                placeholder="Contact Number"
                                size="small"
                                name="contactNumber"
                                disabled={isSent}
                                value={formData.contactNumber}
                                onChange={handleInputChange}
                                error={errors.contactNumber != ""}
                                helperText={errors.contactNumber}
                            />
                        </FormControl>
                        <FormControl size="small" fullWidth sx={{ marginBlock: "5px" }}>
                            <TextField
                                sx={{ marginBlock: "10px" }}
                                required
                                id="outlined-password"
                                label="Password"
                                placeholder="Password"
                                size="small"
                                type="password"
                                name="password"
                                disabled={isSent}
                                value={formData.password}
                                onChange={handleInputChange}
                                error={errors.password != ""}
                                helperText={errors.password}
                            />
                        </FormControl>
                        <FormControl size="small" fullWidth sx={{ marginBlock: "5px" }}>
                            <TextField
                                sx={{ marginBlock: "10px" }}
                                required
                                id="outlined-confirm-password"
                                label="Confirm Password"
                                placeholder="Confirm Password"
                                size="small"
                                type="password"
                                name="confirmPassword"
                                disabled={isSent}
                                value={formData.confirmPassword}
                                onChange={handleInputChange}
                                error={errors.confirmPassword != ""}
                                helperText={errors.confirmPassword}
                            />
                        </FormControl>
                        <FormControl size="small" fullWidth sx={{ marginBlock: "5px", display: isSent ? '' : 'none' }}>
                            <TextField
                                sx={{ marginBlock: "10px" }}
                                required
                                id="outlined-otp"
                                label="OTP"
                                placeholder="OTP"
                                size="small"
                                name="otp"
                                disabled={!isSent}
                                className={props.shouldHide ? 'hidden' : undefined}
                                value={formData.otp}
                                onChange={handleInputChange}
                                error={errors.otp != ""}
                                helperText={errors.otp}
                            />
                        </FormControl>
                        <FormControl
                            size="small"
                            sx={{ marginBlock: "15px", padding: "25px" }}
                        >
                            <Button
                                id="login-submit"
                                variant="contained"
                                onClick={handleSignUp}
                                disabled={loading}
                                size="large"
                                color="primary"
                                disableRipple={false}
                                sx={{ padding: "5px 18px", bgcolor: "#c72825" }}
                            >
                                Register
                            </Button>
                            <Box sx={{ marginTop: "12px" }}>
                                <Link to="/">Back to Login</Link>
                            </Box>
                        </FormControl>
                    </Box>
                </LoadingOverlay>
            </Container>
        </React.Fragment>
    );
};

export default Signup;
